<template>
    <div class="wrapper">
        <div class="row justify-content-end assign-user-row"
             v-if="requiresRight($parleyAdmin.auth.getRightsList().ManageUsers.id)">
            <button class="primary assign-user" @click="openAssignUserModal">Assign new user</button>
        </div>

        <div class="user-list">
            <div v-if="users.length === 0" class="not-found">
                <i class="material-icons">find_in_page</i>
                <span>No users found...</span>
            </div>
            <div v-for="(user, index) in users" class="user-info row" v-bind:key="index">
                <div class="col-2">
                    <div class="name row">
                        {{ user.name }}
                    </div>
                    <div class="email row">
                        {{ user.email }}
                    </div>
                </div>
                <div class="col"></div>
                <div class="col-1 align-self-center">
                    <div class="last-login">
                        Last login: {{ formatLastLogin(user.lastLogin) }}
                    </div>
                </div>
                <i class="edit-user material-icons align-self-center"
                   @click="openEditUserModal(user)"
                   v-if="userIsSuperUser">edit</i>
                <i class="detach-user material-icons align-self-center"
                   @click="openDetachModal(user)"
                   v-if="requiresRight($parleyAdmin.auth.getRightsList().ManageUsers.id)">remove_circle_outline</i>
            </div>
        </div>
        <div v-if="!users">
            No users found...
        </div>

        <section id="assignUserModal">
            <modal ref="assignUserModal">
                <div slot="modal-header">
                    Assign new user to this account
                </div>

                <div slot="modal-content" class="content">
                    <div class="errors">
                        <span v-if="$validator.errors.has('assignUser')">
                            <small v-for="(error, index) in $validator.errors.items" class="error" v-bind:key="index">
                                {{ error.msg }}
                            </small>
                        </span>
                        <small>
                            <a v-if="createNewUserLinkVisible && requiresRight(this.$parleyAdmin.auth.getRightsList().CreateUser.id)" class="row create-user-link" @click="() => { $refs['assignUserModal'].closeModal(); openCreateUserModal(); }">Create new user</a>
                        </small>
                    </div>
                    <custom-input type="text" ref="userEmail" name="user email" class="input-email" label="User email" :validationRules="{ required: true, email: true }" v-model="assignUserEmail"/>
                    <div class="input-rights">
                        <p>Rights</p>
                        <rights-list :new-rights="newRights" :select-all="selectAll"/>
                    </div>
                </div>

                <div slot="modal-footer" class="footer">
                    <div class="" style="z-index: 10">
                        <button class="secondary" @click="$refs['assignUserModal'].closeModal()">Close</button>
                        <button class="primary" @click="validateAssignUser">Assign</button>
                    </div>
                </div>
            </modal>
        </section>

        <section id="detachUserModal">
            <modal ref="detachUserModal">
                <div slot="modal-header">
                    Detach user from this account
                </div>

                <div slot="modal-content" class="content">
                    <div v-if="selectedUser && selectedUser.email === currentUserEmail">
                        Are you sure you want to detach
                        <br>
                        <b>yourself</b>
                        from this account?
                        <br>
                        <div class="important">If you do you will be logged out from this account and lose access!</div>
                        <div class="verify-email">
                            <small>Please enter your email below to verify your choice</small>
                            <custom-input type="text"
                                          ref="verifyUserEmail"
                                          name="verify user email"
                                          class="input-email"
                                          :validationRules="{ required: true, email: true }"
                                          v-model="verifyUserEmail"/>
                        </div>
                    </div>
                    <div v-else>
                        Are you sure you want to detach
                        <br>
                        <b>{{ selectedUser ? selectedUser.email : 'user' }}</b>
                        from this account?
                    </div>

                    <div class="errors" v-if="$validator.errors.has('detachUser')">
                        <small v-for="(error, index) in $validator.errors.items" class="error" v-bind:key="index">
                            {{ error.msg }}
                        </small>
                    </div>
                </div>

                <div slot="modal-footer" class="footer">
                    <div style="z-index: 10">
                        <button class="secondary" @click="$refs['detachUserModal'].closeModal()">No</button>
                        <button class="primary" @click="validateDetachUser">Yes</button>
                    </div>
                </div>
            </modal>
        </section>

        <section id="createUserModal">
            <modal ref="createUserModal">
                <div slot="modal-header">
                    Create a new user
                </div>

                <div slot="modal-content">
                    <div class="errors">
                        <span v-if="$validator.errors.has('createUser')">
                            <small v-for="(error, index) in $validator.errors.items" class="error" v-bind:key="index">
                                {{ error.msg }}
                            </small>
                        </span>
                    </div>
                    <custom-input type="text" ref="newUserEmail" name="user email" label="User email" :validationRules="{ required: true, email: true }" v-model="newUserEmail"/>
                    <custom-input type="text" ref="newUserName" name="user name" label="User name" :validationRules="{ required: true }" v-model="newUserName"/>
                    <div class="input-rights">
                        <p>Rights</p>
                        <rights-list :new-rights="newRights" :select-all="selectAll"/>
                    </div>
                </div>

                <div slot="modal-footer" class="footer">
                    <div style="z-index: 10">
                        <button class="secondary" @click="$refs['createUserModal'].closeModal()">Close</button>
                        <button class="primary" @click="validateNewUser">Create</button>
                    </div>
                </div>
            </modal>
        </section>

        <section id="postCreateUserModal">
            <modal ref="postCreateUserModal">
                <div slot="modal-header">
                    Created user info
                </div>

                <div slot="modal-content">
                    <custom-input type="display" v-model="newUserName" label="Name"/>
                    <custom-input type="display" v-model="newUserEmail" label="Email"/>
                    <custom-input type="secret" v-model="newUserPassword" label="Password" @toggleSecret="showNewUserPassword = !showNewUserPassword" :showSecret="showNewUserPassword"/> <!-- Behaves the same as type=password but this one is readonly -->
                </div>

                <div slot="modal-footer" class="footer">
                    <div style="z-index: 10">
                        <button class="secondary" @click="closePostCreateUserModal">Close</button>
                    </div>
                </div>
            </modal>
        </section>

        <section id="editUserModal" v-if="userIsSuperUser">
            <modal ref="editUserModal">
                <div slot="modal-header">
                    Created user info
                </div>

                <div slot="modal-content" class="content" v-if="selectedUser">
                    <section class="row">
                        <p>Name: <span id="editUserName">{{ selectedUser.name }}</span></p>
                    </section>
                    <section class="row">
                        <p>Email: <span id="editUserEmail">{{ selectedUser.email }}</span></p>
                    </section>
                    <section class="row">
                        <p>Password: <button id="editUserResetPassword" type="button" class="primary" @click="resetPassword(selectedUser.id)">Reset password</button></p>
                    </section>
                    <section class="row">
                        <p>Last login: <span id="editUserLastLogin">{{ formatLastLogin(selectedUser.lastLogin) }}</span></p>
                    </section>
                    <section class="row input-rights">
                        <p>Rights: <button id="editUserChangeRights" type="button" class="primary" @click="openChangeRightsModal()">Change rights</button></p>
                    </section>
                </div>

                <div slot="modal-footer" class="footer">
                    <div style="z-index: 10">
                        <button class="secondary" @click="$refs['editUserModal'].closeModal()">Close</button>
                    </div>
                </div>
            </modal>
        </section>

        <section id="editUserRightsModal">
            <modal ref="editUserRightsModal">
                <div slot="modal-header">
                    User's Rights
                </div>

                <div slot="modal-content">
                    <div class="input-rights">
                        <rights-list :new-rights="newRights" :select-all="selectAll"/>
                    </div>
                </div>

                <div slot="modal-footer" class="footer">
                    <div style="z-index: 10">
                        <button class="secondary" @click="$refs['editUserRightsModal'].closeModal()">Close</button>
                        <button class="primary" @click="updateUserRights(selectedUser.id)">Save</button>
                    </div>
                </div>
            </modal>
        </section>
    </div>
</template>

<script>
import { Default } from '@/mixins/mixins';
import modal from '@/components/additional/modal';
import CustomInput from '@/components/additional/customInput';
import RightsList from './users/rightsList';

export default {
    name: 'users',
    mixins: [
        Default
    ],
    components: {
        RightsList,
        CustomInput,
        modal
    },
    inject: {
        $validator: '$validator' // Get the parent $validator instance
    },
    data() {
        return {
            users: [],
            assignUserEmail: '',
            newUserEmail: '',
            newUserName: '',
            newUserPassword: '',
            showNewUserPassword: false,
            newRights: {
                authorization: {
                    name: 'Authorization',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                statistics: {
                    name: 'Statistics',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                userManagement: {
                    name: 'User management',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                appearance: {
                    name: 'Appearance',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                push: {
                    name: 'Push',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                officeHours: {
                    name: 'Office Hours',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                policies: {
                    name: 'Policies',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                },
                other: {
                    name: 'Other',
                    selectAll: false,
                    indeterminate: false,
                    selected: [],
                    values: []
                }
            },
            selectedUser: null,
            createNewUserLinkVisible: false,
            verifyUserEmail: ''
        };
    },
    created() {
        this.getUsers();

        // Create rights options
        Object.keys(this.$parleyAdmin.auth.getRightsList()).forEach(key => {
            const value = this.$parleyAdmin.auth.getRightsList()[key];

            const groups = {
                authorization: [1, 2, 3],
                statistics: [8],
                userManagement: [5, 6, 7],
                appearance: [4],
                push: [9, 10, 11],
                officeHours: [12],
                policies: [14]
            };

            let groupFound = false;

            Object.keys(groups).forEach(group => {
                if (groups[group].indexOf(value.id) !== -1) {
                    this.newRights[group].values.push({ text: value.description, value: value.id });
                    groupFound = true;
                }
            });

            // All missing ID's will be added to the 'other' group
            if (!groupFound)
                this.newRights.other.values.push({ text: value.description, value: value.id });
        });
    },
    watch: {
        rightsGroups: function(newVal, oldVal) {
            for (let i = 0; i < newVal.length; i++) {
                const val = newVal[i];
                let group = this.newRights[val.key];

                const anythingChanged = newVal[i].selected.length !== oldVal[i].selected.length;

                if (!group && !anythingChanged)
                    continue;

                if (val.selected.length === 0) {
                    group.indeterminate = false;
                    group.selectAll = false;
                } else if (val.selected.length === group.values.length) {
                    group.indeterminate = false;
                    group.selectAll = true;
                } else {
                    group.indeterminate = true;
                    group.selectAll = false;
                }
            }
        }
    },
    computed: {
        identification: {
            get() {
                return this.$store.getters[this.$store.types.loggedinAccount.getters.get].identification;
            }
        },
        rightsGroups: { // This is here to trigger the 'rightsGroups' watcher
            get() {
                let rightsGroups = [];

                Object.keys(this.newRights).forEach(group => {
                    rightsGroups.push({
                        selected: this.newRights[group].selected,
                        key: group
                    });
                });

                return rightsGroups;
            }
        },
        allSelectedRights: {
            get() {
                const groups = this.rightsGroups;
                let allRights = [];
                groups.forEach(group => { group.selected.forEach(right => allRights.push(right)); });
                return allRights;
            }
        },
        currentUserEmail: {
            get() {
                return this.$store.getters[this.$store.types.user.getters.get].email;
            }
        }
    },
    methods: {
        selectAll: function(group) {
            if (!group.selectAll)
                group.values.forEach(_value => group.selected.push(_value.value)); // Add all possible values to the 'selected' list
            else
                group.selected = [];
        },
        deselectAll: function(group) {
            group.selectAll = true;
            this.selectAll(group);
            group.selectAll = false;
        },
        selectOne: function(group, rightID) {
            const optionObject = group.values.filter(_value => _value.value === rightID);
            if (optionObject && optionObject.length === 1)
                group.selected.push(optionObject[0].value);
        },
        validateAssignUser: function() {
            this.$parleyAdmin.validateMultiple([this.$refs.userEmail], value => {
                if (value)
                    this.assignUser(this.assignUserEmail, null, this.allSelectedRights.join());
                else
                    return false;
            });
        },
        validateNewUser: function() {
            this.$parleyAdmin.validateMultiple([this.$refs.newUserEmail, this.$refs.newUserName], value => {
                if (value)
                    this.createUser(this.newUserEmail, this.newUserName);
                else
                    return false;
            });
        },
        validateDetachUser: function() {
            if (this.selectedUser.email !== this.currentUserEmail) {
                this.detachUser(this.selectedUser.id);
                return true;
            }

            // Detaching yourself
            this.$parleyAdmin.validateMultiple([this.$refs.verifyUserEmail], value => {
                if (value)
                    if (this.verifyUserEmail === this.currentUserEmail)
                        this.detachUser(this.selectedUser.id).then(() => {
                            this.$parleyAdmin.auth.logoutAccount();
                            this.$router.push({name: 'accountsList'});
                        });
                    else {
                        this.$validator.errors.clear();
                        this.$validator.errors.add({
                            field: 'detachUser',
                            msg: 'Given email does not match your email'
                        });
                    }
                else
                    return false;
            });
        },
        formatLastLogin: function(lastLoginTimestamp) {
            if (lastLoginTimestamp === null)
                return 'never';

            const now = new Date();
            const then = new Date(lastLoginTimestamp * 1000);
            const hours = Math.floor(Math.abs(now - then) / 36e5);

            return hours > 0 ? `${hours} hours ago` : 'just now';
        },
        getUsers: function() {
            return this.$http.get('v1.1/accounts/users').then(response => {
                // Success callback
                this.users = response.data.data.users;
            }, response => {
                // Error callback
                let notifications = JSON.parse(response.bodyText).notifications;
                if (notifications.filter((element) => { return element.message === 'retrieve_user_from_account_failed'; }).length > 0)
                    this.$parleyAdmin.queryErrors.addError('Something went wrong while retrieving the users list, please try again later');
            });
        },
        getUser: function(userEmail, errorContainer) {
            return new Promise((resolve, reject) => {
                this.$http.get('v1.1/users', {
                    params: {
                        email: userEmail
                    }
                }).then(response => {
                    // Success callback
                    resolve(response.data.data.user);
                }, response => {
                    // Error callback
                    this.$validator.errors.clear();
                    let notifications = JSON.parse(response.bodyText).notifications;
                    if (notifications.filter((element) => { return element.message === 'user_not_found'; }).length > 0)
                        this.$validator.errors.add({
                            field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                            msg: 'No user found with this email'
                        });
                    reject(new Error('user_not_found'));
                });
            });
        },
        openAssignUserModal: function() {
            this.$validator.errors.clear();
            this.$refs['userEmail'].$validator.errors.clear();
            this.assignUserEmail = '';
            this.createNewUserLinkVisible = false;
            this.$refs['assignUserModal'].openModal();
        },
        openDetachModal: function(user) {
            this.$validator.errors.clear();
            if (this.$refs['verifyUserEmail']) { // If you are not detaching yourself, this ref does not exist
                this.$refs['verifyUserEmail'].$validator.errors.clear();
                this.verifyUserEmail = '';
            }
            this.selectedUser = user;
            this.$refs['detachUserModal'].openModal();
        },
        openCreateUserModal: function() {
            this.$validator.errors.clear();
            this.$refs['newUserEmail'].$validator.errors.clear();
            this.$refs['newUserName'].$validator.errors.clear();
            this.$refs['createUserModal'].openModal();
            this.newUserEmail = this.assignUserEmail || '';
            this.newUserName = this.newUserEmail ? this.newUserEmail.split('@')[0] : '';
        },
        openEditUserModal: function(user) {
            this.selectedUser = user;
            this.$refs['editUserModal'].openModal();
        },
        closePostCreateUserModal: function() {
            this.$refs['postCreateUserModal'].closeModal();
            this.newUserEmail = '';
            this.newUserName = '';
            this.newUserPassword = '';
        },
        openChangeRightsModal: function() {
            this.$http.get('v1.1/rights/' + this.selectedUser.id, {}).then(response => {
                // Success callback
                Object.keys(this.newRights).filter(groupIndex => {
                    let group = this.newRights[groupIndex]; // Current group object
                    this.deselectAll(group); // Mark everything in this group as not-selected
                });

                response.data.data.rights.forEach(rightId => {
                    Object.keys(this.newRights).filter(groupIndex => {
                        let group = this.newRights[groupIndex]; // Current group object
                        this.selectOne(group, rightId); // Mark as right option as selected (if it is in this group)
                    });
                });
                this.$refs['editUserRightsModal'].openModal();
            }, response => {
                // Error callback
                this.$parleyAdmin.queryErrors.addError('Something went wrong, please try again later');
            });
        },
        assignUser: async function(userEmail, userId, rightIds, errorContainer) {
            if (!errorContainer)
                errorContainer = 'assignUser';

            let id = null;
            if (userId)
                id = userId;
            else if (userEmail) {
                const user = await this.getUser(userEmail, errorContainer).catch(e => {
                    if (e.message === 'user_not_found')
                        this.createNewUserLinkVisible = true;
                });
                id = user ? user.id : null;
            }

            if (!id)
                return;

            // Assign user to account
            return this.$http.post(`v1.1/users/${id}/account`, {
                rightIds: rightIds
            }).then(response => {
                // Success callback
                this.$refs.assignUserModal.closeModal();
                this.getUsers().then(() => {
                    // Show success after api call, because all apicalls clear errors
                    this.$parleyAdmin.queryErrors.addError('Successfully added user to this account', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
                });
            }, response => {
                // Error callback
                this.$validator.errors.clear();
                let notifications = JSON.parse(response.bodyText).notifications;
                if (notifications.filter((element) => { return element.message === 'user_not_found'; }).length > 0)
                    this.$validator.errors.add({
                        field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                        msg: 'No user found with this email'
                    });
                if (notifications.filter((element) => { return element.message === 'retrieving_user_failed'; }).length > 0)
                    this.$validator.errors.add({
                        field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                        msg: 'Something went wrong, please try again later'
                    });
                if (notifications.filter((element) => { return element.message === 'account_not_found'; }).length > 0)
                    this.$validator.errors.add({
                        field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                        msg: 'Something went wrong, please logout from this account and log back in'
                    });
                if (notifications.filter((element) => { return element.message === 'user_already_attached_to_account'; }).length > 0)
                    this.$validator.errors.add({
                        field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                        msg: 'This user is already attached to this account'
                    });
            });
        },
        detachUser: function(userId) {
            return this.$http.delete(`v1.1/users/${userId}/account`)
                .then(response => {
                    // Success callback
                    this.$refs.detachUserModal.closeModal();
                    this.getUsers().then(() => {
                        // Show success after api call, because all apicalls clear errors
                        this.$parleyAdmin.queryErrors.addError('Successfully detached user from this account', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
                    });
                }, response => {
                    // Error callback
                    this.$validator.errors.clear();
                    let notifications = JSON.parse(response.bodyText).notifications;
                    if (notifications.filter((element) => { return element.message === 'detach_user_from_account_failed'; }).length > 0)
                        this.$validator.errors.add({
                            field: 'detachUser', // Not used in the traditional way, check the error div in the <template>
                            msg: 'Something went wrong, please try again later'
                        });
                });
        },
        createUser: function(userEmail, userName) {
            const errorContainer = 'createUser';
            this.$http.post('v1.1/users', {
                name: userName,
                email: userEmail
            }).then(response => {
                // Success callback
                const userId = response.data.data.user.id;
                this.newUserEmail = response.data.data.user.email;
                this.newUserName = response.data.data.user.name;
                this.newUserPassword = response.data.data.user.password;
                // Assign user to account
                this.assignUser(null, userId, this.allSelectedRights.join(), errorContainer).then(() => {
                    this.$refs['createUserModal'].closeModal();
                    // Open modal with created user info
                    this.$refs['postCreateUserModal'].openModal();
                });
            }, response => {
                // Error callback
                this.$validator.errors.clear();
                let notifications = JSON.parse(response.bodyText).notifications;
                if (notifications.filter((element) => { return element.message === 'email_already_exists'; }).length > 0)
                    this.$validator.errors.add({
                        field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                        msg: 'This email is already in use'
                    });
                if (notifications.filter((element) => { return element.message === 'creating_user_failed'; }).length > 0)
                    this.$validator.errors.add({
                        field: errorContainer, // Not used in the traditional way, check the error div in the <template>
                        msg: 'Something went wrong, please try again later'
                    });
            });
        },
        updateUserRights: function(userId) {
            this.$http.put('v1.1/users/' + userId + '/account', {
                rightIds: this.allSelectedRights.join()
            }).then(response => {
                // Success callback
                this.$parleyAdmin.queryErrors.addError('Successfully updated user\'s rights', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
                this.$refs['editUserRightsModal'].closeModal();
            }, response => {
                // Error callback
                this.$parleyAdmin.queryErrors.addError('Something went wrong, please try again later');
            });
        },
        resetPassword: function(userID) {
            if (userID === this.$store.getters[this.$store.types.user.getters.get].id)
                this.$router.push({name: 'profile'}); // You can't change your own password from here, so redirect to profile page
            else
                this.$http.put('v1.1/users', {
                    resetPassword: true,
                    userId: userID
                }).then(response => {
                    // Success callback
                    this.newUserEmail = response.data.data.user.email;
                    this.newUserName = response.data.data.user.name;
                    this.newUserPassword = response.data.data.user.newPassword;

                    this.$refs['editUserModal'].closeModal();
                    this.$parleyAdmin.queryErrors.addError('Successfully reset user\'s password', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);

                    // Open modal with created user info
                    this.$refs['postCreateUserModal'].openModal();
                }, response => {
                    // Error callback
                    this.$parleyAdmin.queryErrors.addError('Something went wrong, please try again later');
                });
        }
    }
};
</script>

<style lang="scss" scoped>
    @import "../assets/styles/variables";

    $wrapperPadding: 1em;
    .wrapper {
        padding: $wrapperPadding;
        max-height: inherit;

        $assignUseRowHeight: 3em;
        .assign-user-row {
            height: $assignUseRowHeight;

            .assign-user {
                height: auto;
                margin: 0 1em;
                line-height: unset;
            }
        }

        .detach-user,
        .edit-user {
            color: #9d9d9d;
            cursor: pointer;
            font-size: 1.5em;
            margin: .25em;
        }

        .detach-user:hover {
            color: #f5222d;
        }

        .edit-user:hover {
            color: $primary-color;
        }

        #assignUserModal {
            .content {
                width: 28em;

                .input-email {
                    /*max-width: 20em;*/
                }

                .input-rights {
                    p {
                        font-weight: 550;
                    }
                }
            }
        }

        #detachUserModal {
            .content {
                /*text-align: center;*/
                width: 20em;

                .important {
                    margin: 1em 0;
                    font-size: .8em;
                    color: red;
                }
            }
        }

        #editUserModal {
            .content {
                margin: 1em 0;
                width: 20em;

                section {
                    margin-bottom: 1em;

                    label {
                        margin-right: 1em;
                    }

                    p {
                        span {
                            color: $secondary-color;
                        }
                    }
                }
            }
        }

        $userListMarginTop: 1em;
        .user-list {
            max-height: calc(100vh - #{$topBarHeight} - #{$assignUseRowHeight} - #{$wrapperPadding} - #{$userListMarginTop});
            overflow: auto;
            margin-top: $userListMarginTop;

            .user-info {
                background-color: white;
                padding: 1em;
                margin: 1em;

                .email {
                    color: #9d9d9d;
                    font-size: .8em;
                }

                .last-login {
                    color: #9d9d9d;
                    font-size: .7em;
                    /*font-size: 10px;*/
                    margin: .5em 0;
                }
            }

            .not-found {
                width: 100%;
                height: 50vh;
                text-align: center;
                margin-top: 10em;

                .material-icons {
                    color: $tertiary-color;
                    font-size: 5em;
                }

                span {
                    position: relative;
                    top: -1.8em;
                    user-select: none;
                }
            }
        }

        .errors {
            margin: 0 0 1em 0;

            .error {
                color: red;
            }

            .create-user-link {

            }
        }
    }
</style>
